import React from "react";

import { flattenChildren } from "./flattenChildren";
import { buildSlots, isChildOfType, SlotConfig, SlotValue } from "./slotUtils";

type SlotElements<Config extends SlotConfig> = {
  [Property in keyof Config]: SlotValue<Config, Property>;
};

/**
 * Extract components from `children` so we can render them in different places,
 * allowing us to implement components with SSR-compatible slot APIs.
 * Note: We can only extract direct children, not nested ones.
 */
export function getSlots<Config extends SlotConfig>(
  children: React.ReactNode,
  config: Config
): [Partial<SlotElements<Config>>, React.ReactNode[]] {
  // Object mapping slot names to their elements
  const slots: Partial<SlotElements<Config>> = buildSlots(
    config,
    () => undefined
  );

  // Array of elements that are not slots
  const rest: React.ReactNode[] = [];

  const keys = Object.keys(config) as Array<keyof Config>;
  const values = Object.values(config);

  flattenChildren(children).forEach((child) => {
    const index = values.findIndex((value) => isChildOfType(child, value));

    // If the child is not a slot, add it to the `rest` array
    if (index === -1) {
      rest.push(child);
      return;
    }

    const slotKey = keys[index] as keyof Config;

    // If slot is already filled, ignore duplicates
    if (slots[slotKey]) {
      // oxlint-disable-next-line no-console
      console.warn(
        `Found duplicate "${String(slotKey)}" slot. Only the first will be rendered.`
      );
      return;
    }

    // If the child is a slot, add it to the `slots` object
    slots[slotKey] = child as SlotValue<Config, keyof Config>;
  });

  return [slots, rest];
}
